import { Injectable, Output, EventEmitter, signal, Injector, Renderer2 } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { HttpService } from './http.service';
import { LocalStorageService } from './local-storage.service';
import { AppSettingConfig, AwsImageOption } from '@app/app-types';
import { APP_CONFIG_KEY, IMAGES_URL } from '@app/core/constants/system-list.constant';
import { ROUTES, SERVER_URL, TENANT_INFO, TIMEZONE } from '../constants';
import { CoreClass } from '../base/core.class';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
// import { APIContracts, APIControllers } from 'authorizenet';
// import * as Authorize from 'authorizenet';

@Injectable({
  providedIn: 'root',
})
export class SharedDataService {
  public pharmacyInfo: any = {
    drugName: "",
    zipCode: "",
    isSelected : false
  }
  public pharmacyInput = ""
  public filesPath = "/assets/files/";
  public removeNavbarLinks = false
  public familyMemberId: number = 0
  public currentEvent: any
  public successHeading = ""
  public successDescription = ""
  public successDate = "";
  public tenantName: any = null
  public noMoreChildren = false;
  public numberOfChildren = 0;
  public appointments = true
  public user = signal(false)
  public imagesUrl = IMAGES_URL
  public resetPasswordForm = new Subject()
  public loadingBarSource = new BehaviorSubject(false);
  private formSubmitButtonSource = new BehaviorSubject(false);
  public loadingBarSourceReset = new BehaviorSubject(false);
  public searcgBarValue = new BehaviorSubject('');
  loadingBarStatus = this.loadingBarSource.asObservable();

  public appConfigSource: BehaviorSubject<AppSettingConfig> = new BehaviorSubject<AppSettingConfig>(
    this.getDefaultAppConfig(),
  );
  appConfig$ = this.appConfigSource.asObservable();

  public hideNavBar$ = new BehaviorSubject(false);
  public inferenceImageUploadingSource = new BehaviorSubject(false);
  public batchInferenceImageUploadingSource = new BehaviorSubject(false);

  public datasetTotalSelectedFilesCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public datasetTotalUploadedFilesCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public appInstanceId$: BehaviorSubject<null | string> = new BehaviorSubject<null | string>(null);
  public datasetImageUploadAppInstanceId$: BehaviorSubject<null | string> = new BehaviorSubject<
    null | string
  >(null);
  public currentFileUploadDatasetId$: BehaviorSubject<null | number> = new BehaviorSubject<
    number | null
  >(null);
  public datasetImageList$: BehaviorSubject<AwsImageOption[]> = new BehaviorSubject<
    AwsImageOption[]
  >([]);

  public currentLanguage: any = 'en';
  public currentCurrency = {
    name: "dollar",
    symbol: "$",
    localStringFormat: "en-US"
  }
  public globalMembershipError = new Subject();
  private renderer: Renderer2 | any
  public results : any = []

  @Output() toggleShowWidget: EventEmitter<boolean> = new EventEmitter<boolean>(true);
  @Output() buttonClick: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    public httpService: HttpService,
    public localService: LocalStorageService,
    public router: Router,
    public httpClient: HttpClient,
  ) {
    // Setting user language
    const lang: any = this.localService.get('language', false)?.code?.toLowerCase() || 'en';
    this.setUserLanguage(lang || 'en');
    this.currentLanguage = lang || 'en';
  }
  selectPharmacy(item: any) {
    this.pharmacyInfo = {...this.pharmacyInfo,...item}
    this.pharmacyInput = item.drugName
    this.pharmacyInfo.isSelected = true
    this.results = []
  }
  routeToSearch() {
    if (this.pharmacyInfo.isSelected) {
    this.results = []
      this.router.navigate(
        [ROUTES.pharmacyList],
        { queryParams: { drug: this.pharmacyInfo.drugName, drugType: this.pharmacyInfo.drugType, zip_code: this.pharmacyInfo.zipCode?.length > 0 ? this.pharmacyInfo.zipCode : 48243 } }
      );
    }
  }
  /**
   * the following method is used to emit for loading bar
   * @param status
   */
  showLoadingBar(status: boolean) {
    setTimeout(() => {
      this.loadingBarSource.next(status);
    }, 150);
  }

  /**
   * the following method is used to emit for loading bar
   * @param submit
   */
  changeFormSubmitStatus(submit: boolean) {
    setTimeout(() => {
      this.formSubmitButtonSource.next(submit);
    }, 150);
  }

  /**
   * the following method is used to set the app theme mode
   * @param config
   */
  setAppConfig(config: AppSettingConfig = this.getDefaultAppConfig()): void {
    if (JSON.stringify(this.appConfigSource.value) !== JSON.stringify(config)) {
      this.appConfigSource.next(config);
      this.localService.set({
        key: APP_CONFIG_KEY,
        value: config,
      });
    }
  }

  getDefaultAppConfig(): any {
    const lang: any = this.localService.get('language', false)?.code?.toLowerCase() || 'en';
    return <AppSettingConfig>{
      theme: 'dark',
      locale: lang || 'en',
      isSideBarCollapse: false,
      language: this.getDefaultLanguage(),
      layoutDirection: lang === 'ar' ? 'rtl' : 'ltr',
    };
  }

  /**
   * the following method is used to set user language
   * @param language
   */
  setUserLanguage(language: any = null): void {
    if (language?.code && ['en', 'ar'].indexOf(language.code.toLowerCase()) === -1) {
      language = this.getDefaultLanguage();
    }
    const appConfig = { ...this.appConfigSource.value };
    appConfig.language = language;
    appConfig.locale = language.code;
    this.setAppConfig(appConfig);
  }

  getTranslatedText(data: any, key: string) {
    if (!data) {
      return '';
    }
    const lang: any = this.localService.get('language', false)?.code?.toLowerCase() || 'en';
    const locale = lang || 'en';
    const capitalizeLocale = locale.charAt(0).toUpperCase() + locale.slice(1);
    const text = data[`${key}${capitalizeLocale}`];
    return text || data[key] || data[`${key}En`] || '';
  }

  public clearAwsFileListData(): void {
    this.datasetImageList$!.next([]);
  }

  public initAppInstanceId(): void {
    this.appInstanceId$.next(uuid());
  }

  public isDatasetUploadingImageInDifferentTab(): boolean {
    return this.currentFileUploadDatasetId$.value && !this.datasetImageUploadAppInstanceId$.value
      ? true
      : false;
  }

  public isDatasetImageUploadingInCurrentAppInstance(): boolean {
    return this.appInstanceId$.value &&
      this.datasetImageUploadAppInstanceId$.value &&
      this.currentFileUploadDatasetId$.value &&
      this.appInstanceId$.value === this.datasetImageUploadAppInstanceId$.value
      ? true
      : false;
  }

  private getDefaultLanguage(): any {
    const lang: any = this.localService.get('language', false)?.code?.toLowerCase() || 'en';
    return {
      id: null,
      code: lang || 'en',
    };
  }

  public addToCart(redirectUrl?: any): any {

    let endpoint = SERVER_URL + `/cart/item/add`;
    let data = JSON.parse(localStorage.getItem('addToCartData') as any);

    if (!localStorage.getItem('token')) {
      localStorage.setItem('redirectionUrl', '/payment');
      this.router.navigateByUrl('auth/login');
    } else {
      this.router.navigateByUrl('/payment');
    }
    // this.httpService.requestEntity('POST', endpoint, data, undefined, false)
    // // .pipe(takeUntil(this.onDestroy$))
    // .subscribe((res)=> {
    //    let response = res.body;
    //    localStorage.setItem('cartId', response.data.cartId);
    //    localStorage.removeItem('addToCartData');
    //    this.router.navigateByUrl(redirectUrl ? redirectUrl : 'payment');     
    // });
    // }

  }
  getTimeZones = () => {
    return this.httpService.requestEntity('get', TIMEZONE)
  }
  addLoader = (event: any) => {
    let i = document.createElement("i")
    const classes = ["fa", "fa-circle-o-notch", "fa-spin", "me-2"]
    i.classList.add(...classes)
    event.currentTarget.prepend(i)
    event.currentTarget.disabled = true
    this.currentEvent = event.currentTarget
  }
  removeLoader = () => {
    this.currentEvent.disabled = false
    this.currentEvent.children[0].remove()
  }
  getTenantInfo = () => {
    if (!this.localService.getDataInLocalStorage("tenantInfo")) {
      this.httpClient.get(TENANT_INFO + "?domain=lassy.com")
        .subscribe(
          (response: any) => {
            this.tenantName = response.data.name
            this.localService.setDataInLocalStorage({ key: "tenantInfo", value: response.data })

          }
        )
    }
    else {
      this.tenantName = this.localService.getDataInLocalStorage("tenantInfo").name
    }
  }
  styleChange = (color: string) => {
    const style = this.renderer.createElement('style');
    style.type = 'text/css';
    style.id = 'consumercss';
    style.innerHTML = `:root {
					--primarycolor: `+ color + `;
					--primarycolorhover: `+ color + `;
				}`;
    this.renderer.appendChild(document.head, style);
  }
}
